import { SCORE_UPDATE_GRACE_PERIOD_SECONDS, WS_EVENT } from '@shared/js/websocket/constants';
import ws from '@shared/js/websocket/websocket-client';
import playerTypes from '@videotools/js/constants/playerTypes';
import * as MUTATION from '../../constants/mutations';

const enableLogging = true;

const log = (...args) => {
	if (enableLogging) console.log('[SYNC]', ...args);
};

const isWithinScoreUpdateWindow = (event, unixNow = Math.floor(Date.now() / 1000)) =>
	event.startTimestamp <= unixNow && unixNow < event.endTimestamp + SCORE_UPDATE_GRACE_PERIOD_SECONDS;

const getInitialState = () => ({
	ws:                      null,
	eventSubscription:       null,
	eventStreamSubscription: null,
	eventLineupSubscription: null,
	scoreSubscriptions:      [],
	eventTagsSubscriptions:  [],
	eventTagsActiveChannel:  null,
});

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		[MUTATION.SET_STATE]: (state, payload) => {
			for (const [k, v] of Object.entries(payload)) {
				state[k] = v;
			}
		},
		[MUTATION.SET_SYNC_WS]: (state, value) => {
			state.ws = value;
		},
		[MUTATION.SET_EVENT_SUBSCRIPTION]: (state, value) => {
			state.eventSubscription = value;
		},
		[MUTATION.SET_EVENT_STREAM_SUBSCRIPTION]: (state, value) => {
			state.eventStreamSubscription = value;
		},
		[MUTATION.SET_EVENT_LINEUP_SUBSCRIPTION]: (state, value) => {
			state.eventLineupSubscription = value;
		},
		[MUTATION.ADD_SCORE_SUBSCRIPTION]: (state, { eventId, subscription, expiryTimer }) => {
			state.scoreSubscriptions = [...state.scoreSubscriptions, { eventId, subscription, expiryTimer }];
		},
		[MUTATION.REMOVE_SCORE_SUBSCRIPTION]: (state, { eventId }) => {
			state.scoreSubscriptions = state.scoreSubscriptions.filter(({ eventId: _eventId }) => _eventId !== eventId);
		},
		[MUTATION.SET_EVENT_TAGS_SUBSCRIPTIONS]: (state, subscriptions) => {
			state.eventTagsSubscriptions = Array.from(subscriptions);
		},
		[MUTATION.SET_EVENT_TAGS_ACTIVE_CHANNEL]: (state, channel) => {
			state.eventTagsActiveChannel = channel;
		},
	},
	actions: {
		setState: ({ commit }, value) => commit(MUTATION.SET_STATE, value),
		init:     ({ commit }) => {
			log('Sync init.');
			ws.init();
			commit(MUTATION.SET_SYNC_WS, ws);
		},
		setup: ({ dispatch, rootGetters }) => {
			const event = rootGetters['event/event'];
			const isTaggingTool = rootGetters['app/isTaggingToolMode'];

			dispatch('registerEventListener', event.id);
			dispatch('registerEventTagsListener', event.id);
			if (event.streams && Array.isArray(event.streams)) {
				for (const stream of event.streams) {
					dispatch('registerEventStreamListener', stream.id);
				}
			}

			// Listen for lineup changes on arena player
			if (!isTaggingTool) {
				dispatch('registerEventLineupListener', event.id);
			}
		},
		// event
		registerEventListener: ({ state, dispatch, commit, rootGetters }, eventId) => {
			const isTaggingTool = rootGetters['app/isTaggingToolMode'];

			let channel = `event.${eventId}`;
			if (!isTaggingTool) {
				channel = `event.${eventId}.viewer`;
			}

			const event = WS_EVENT.EVENT_UPDATE;

			log('Register event listener:', eventId);

			if (state.eventSubscription) {
				state.ws.unsubscribe(state.eventSubscription);
			}

			const subscription = state.ws.subscribe({
				channel,
				event,
				callback: (data) => dispatch('updateEventCallback', { event: data.event, patch: true }),
			});
			commit(MUTATION.SET_EVENT_SUBSCRIPTION, subscription);
		},
		updateEventCallback: ({ dispatch }, { event, patch = false }) => {
			log('Update event callback:', { event, patch });

			dispatch('event/handleEventUpdate', { data: event, patch }, { root: true });
		},
		// event stream
		registerEventStreamListener: ({ state, dispatch, commit }, streamId) => {
			const channel = `stream.${streamId}`;
			const event = WS_EVENT.STREAM_UPDATE;

			log('Register event stream listener:', { streamId });

			if (state.eventStreamSubscription) {
				state.ws.unsubscribe(state.eventStreamSubscription);
			}

			const subscription = state.ws.subscribe({
				channel,
				event,
				callback: (data) => {
					dispatch('updateEventStreamCallback', data);
				},
			});
			commit(MUTATION.SET_EVENT_STREAM_SUBSCRIPTION, subscription);
		},
		updateEventStreamCallback: ({ dispatch, rootGetters }, data) => {
			log('Update event stream callback:', { data });

			dispatch('error/setPlayerError', null, { root: true });

			const event = rootGetters['event/event'];
			if (event) {
				const index = event.streams.findIndex((stream) => stream.id === data.stream.id);
				if (index > -1) {
					const stream = event.streams[index];
					if (stream && stream.startedAtTimestamp && stream.startedAtTimestamp !== data.stream.startedAtTimestamp) {
						dispatch('event/streamStartChanged', {}, { root: true });
					}

					Object.assign(stream, data.stream);
					dispatch('event/setStream', { index, stream }, { root: true });
					if (data.hlsUrlChanged) {
						// Fetch a fresh protected URL when the backend reports a playout-related change.
						dispatch('event/loadEventStreamPlayout', event.id, { root: true });
					}
				}
			}
		},
		// event tags
		registerEventTagsListener: ({ state, commit, dispatch, rootGetters }, eventId) => {
			const selectedTagGroup = rootGetters['tagGroups/selectedTagGroup'];
			const channel = selectedTagGroup ? selectedTagGroup.broadcastChannel : `event.${eventId}.tags`;

			if (channel === state.eventTagsActiveChannel && state.eventTagsSubscriptions.length > 0) {
				// already subscribed
				return;
			}
			const events = [WS_EVENT.EVENT_TAG_CREATE, WS_EVENT.EVENT_TAG_DELETE, WS_EVENT.EVENT_TAG_UPDATE];

			// unregister any existing listeners
			dispatch('unregisterEventTagsListener');

			const subscriptions = [];
			for (const event of events) {
				const subscription = state.ws.subscribe({
					channel,
					event,
					callback: (data) => {
						dispatch('updateEventTagsCallback', { data, event });
					},
				});
				subscriptions.push(subscription);
			}

			commit(MUTATION.SET_EVENT_TAGS_SUBSCRIPTIONS, subscriptions);
			commit(MUTATION.SET_EVENT_TAGS_ACTIVE_CHANNEL, channel);
		},
		unregisterEventTagsListener: ({ state, commit }) => {
			for (const subscription of state.eventTagsSubscriptions) {
				state.ws.unsubscribe(subscription);
			}
			commit(MUTATION.SET_EVENT_TAGS_SUBSCRIPTIONS, []);
		},
		updateEventTagsCallback: ({ dispatch }, { event, data }) => {
			log('Update event tags callback:', { event, data });

			const tag = data?.eventTag;
			if (!tag) throw new Error(`Invalid tag: ${tag}`);

			dispatch('event/handleEventTagUpdate', { event, tag }, { root: true });
		},
		// event score
		setupEventScoreListener({ state, dispatch, rootGetters }, _eventId) {
			const isLeaguePlayer = rootGetters['app/playerType'] === playerTypes.LEAGUE;
			if (isLeaguePlayer) {
				const matchdayEvents = rootGetters['event/matchdayEvents'];
				const eligibleEvents = matchdayEvents.filter(event => isWithinScoreUpdateWindow(event));
				const eligibleEventIds = new Set(eligibleEvents.map(event => event.id));

				for (const { eventId } of state.scoreSubscriptions) {
					if (!eligibleEventIds.has(eventId)) dispatch('unregisterEventScoreListener', eventId);
				}
				for (const event of eligibleEvents) {
					dispatch('registerEventScoreListener', event);
				}
			}
		},
		registerEventScoreListener: ({ state, dispatch, commit }, event) => {
			if (state.scoreSubscriptions.some(({ eventId }) => eventId === event.id)) return;

			const channel = `event.${event.id}.scores`;
			const subscription = state.ws.subscribe({
				channel,
				event:    WS_EVENT.SCORE_UPDATED,
				callback: (data) => dispatch('updateEventScoreCallback', { event, data }),
			});
			const expiresInMilliseconds = Math.max(0, (event.endTimestamp + SCORE_UPDATE_GRACE_PERIOD_SECONDS - Math.floor(Date.now() / 1000)) * 1000);
			const expiryTimer = setTimeout(async() => {
				try {
					// Recover a broadcast missed during a disconnect before dropping the listener.
					await dispatch('event/loadMatchdayEvents', true, { root: true });
				} finally {
					dispatch('unregisterEventScoreListener', event.id);
				}
			}, expiresInMilliseconds);

			commit(MUTATION.ADD_SCORE_SUBSCRIPTION, { eventId: event.id, subscription, expiryTimer });
		},
		unregisterEventScoreListener: ({ state, commit }, eventOrId) => {
			const eventId = typeof eventOrId === 'object' ? eventOrId.id : eventOrId;
			const scoreSubscription = state.scoreSubscriptions.find(subscription => subscription.eventId === eventId);
			if (!scoreSubscription) return;

			clearTimeout(scoreSubscription.expiryTimer);
			state.ws.unsubscribe(scoreSubscription.subscription);
			commit(MUTATION.REMOVE_SCORE_SUBSCRIPTION, { eventId });
		},
		updateEventScoreCallback: ({ dispatch }, { event, data }) => {
			log('Update event score callback:', { event, data });
			dispatch('event/updateScore', { event, score: data.score, finalScore: data.finalScore, scores: data.scores }, { root: true });
		},
		// event lineup
		registerEventLineupListener({ state, dispatch, commit }, eventId) {
			const channel = `event.${eventId}.lineup`;
			const event = WS_EVENT.EVENT_LINEUP_IMPORTED;

			log('Register event lineup listener:', { eventId });

			if (state.eventLineupSubscription) {
				state.ws.unsubscribe(state.eventLineupSubscription);
			}

			const subscription = state.ws.subscribe({
				channel,
				event,
				callback: (data) => {
					dispatch('updateEventLineupCallback', data);
				},
			});
			commit(MUTATION.SET_EVENT_STREAM_SUBSCRIPTION, subscription);
		},
		updateEventLineupCallback: ({ commit }, data) => {
			log('Update event lineup callback:', { data });

			if (data.eventLineup) {
				commit(`event/${MUTATION.SET_EVENT_LINEUP}`, data.eventLineup, { root: true });
			}
		},
	},
	getters: {},
};
